import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  FiUsers,
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiShield,
  FiUserCheck,
  FiUserX,
  FiDollarSign,
  FiClock,
  FiMail,
  FiPhone,
  FiMapPin,
  FiAward,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
  FiDownload,
  FiMoreVertical,
} from "react-icons/fi";
import { BsWallet2, BsGraphUp } from "react-icons/bs";
import { Helmet } from "react-helmet-async";
import {
  fetchAdminUsers,
  fetchUserById,
  updateUser,
  deleteUser,
  clearSelectedUser,
} from "../../store/slices/adminSlice";
import { GlassCard } from "../../components/ui/GlassCard";
import { LoadingSkeleton } from "../../components/ui/LoadingSkeleton";
import { format } from "date-fns";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export const AdminUsersPage = () => {
  const dispatch = useDispatch();
  const {
    adminUsers,
    adminUsersTotals,
    adminUsersPagination,
    selectedUser,
    isLoading,
  } = useSelector((state) => state.admin);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    username: "",
    email: "",
    role: "",
    balance: 0,
  });

  const loadUsers = () => {
    dispatch(
      fetchAdminUsers({
        page,
        limit: 100,
        searchTerm: searchTerm || undefined,
        role: roleFilter !== "all" ? roleFilter : undefined,
        sortBy,
      }),
    );
  };

  useEffect(() => {
    loadUsers();
  }, [dispatch, page, searchTerm, roleFilter, sortBy]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [searchTerm, roleFilter, sortBy]);

  useEffect(() => {
    if (selectedUserId) {
      dispatch(fetchUserById(selectedUserId));
    }
  }, [dispatch, selectedUserId]);

  useEffect(() => {
    if (selectedUser) {
      setEditFormData({
        username: selectedUser.user?.username || "",
        email: selectedUser.user?.email || "",
        role: selectedUser.user?.role || "",
        balance: selectedUser.user?.balance || 0,
      });
    }
  }, [selectedUser]);

  const displayedUsers = adminUsers || [];

  const handleViewUser = (userId) => {
    setSelectedUserId(userId);
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUserId(user._id);
    setEditFormData({
      username: user.username,
      email: user.email,
      role: user.role,
      balance: user.balance,
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    try {
      await dispatch(
        updateUser({
          id: selectedUserId,
          data: editFormData,
        }),
      );
      toast.success("User updated successfully");
      setShowEditModal(false);
      loadUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async () => {
    try {
      await dispatch(deleteUser(selectedUserId));
      toast.success("User deleted successfully");
      setShowDeleteModal(false);
      setShowUserModal(false);
      loadUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const exportToExcel = () => {
    const data = displayedUsers.map((u) => ({
      Username: u.username,
      Email: u.email,
      Role: u.role,
      Balance: `₦${u.balance?.toLocaleString() || 0}`,
      "Join Date": format(new Date(u.createdAt), "MMM dd, yyyy"),
      "Total Orders": u.orders?.length || 0,
      "Total Spent": `₦${u.orders?.reduce((sum, o) => sum + (o.amount || 0), 0).toLocaleString() || 0}`,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `users_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-purple-100 text-purple-700";
      case "user":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-luxury-100 text-luxury-700";
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Manage Users | Admin | SocialMarket</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-8"
          >
            <h1 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              Manage <span className="gradient-text">Users</span>
            </h1>
            <p className="text-luxury-600">
              View and manage all user accounts on the platform
            </p>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FiUsers className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  {adminUsersTotals?.totalUsers ?? 0}
                </h3>
                <p className="text-sm text-luxury-900">Total Users</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FiShield className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  {adminUsersTotals?.totalAdmins ?? 0}
                </h3>
                <p className="text-sm text-luxury-900">Admins</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <FiUserCheck className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  {adminUsersTotals?.totalRegularUsers ?? 0}
                </h3>
                <p className="text-sm text-luxury-900">Regular Users</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <BsWallet2 className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  ₦{(adminUsersTotals?.totalBalance || 0).toLocaleString()}
                </h3>
                <p className="text-sm text-luxury9500">Total Balance</p>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Filters Bar */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="glass rounded-2xl p-4 mb-8"
          >
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                <input
                  type="text"
                  placeholder="Search by username or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-luxury pl-12 w-full"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-6 py-3 rounded-xl flex items-center space-x-2 transition-all duration-300 ${
                  showFilters
                    ? "bg-primary-600 text-white"
                    : "glass hover:bg-primary-50 text-luxury-700"
                }`}
              >
                <FiFilter className="w-5 h-5" />
                <span>Filters</span>
                <FiChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${
                    showFilters ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Export Button */}
              <button
                onClick={exportToExcel}
                className="px-6 py-3 glass rounded-xl flex items-center space-x-2 hover:bg-primary-50 transition-colors"
              >
                <FiDownload className="w-5 h-5" />
                <span>Export</span>
              </button>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-6 mt-4 border-t border-luxury-200">
                    <div className="grid md:grid-cols-3 gap-4">
                      {/* Role Filter */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Role
                        </label>
                        <select
                          value={roleFilter}
                          onChange={(e) => setRoleFilter(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="all">All Roles</option>
                          <option value="admin">Admin</option>
                          <option value="user">User</option>
                        </select>
                      </div>

                      {/* Sort By */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Sort By
                        </label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="name_asc">Name: A to Z</option>
                          <option value="name_desc">Name: Z to A</option>
                          <option value="balance_high">
                            Balance: High to Low
                          </option>
                          <option value="balance_low">
                            Balance: Low to High
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Users Table */}
          {isLoading ? (
            <LoadingSkeleton count={5} />
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="glass rounded-2xl overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-luxury-100">
                    <tr className="text-left text-sm text-luxury-600">
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4">Balance</th>
                      <th className="px-6 py-4">Joined</th>
                      <th className="px-6 py-4">Orders</th>
                      <th className="px-6 py-4">Total Spent</th>
                      <th className="px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedUsers.map((user) => (
                      <motion.tr
                        key={user._id}
                        variants={fadeInUp}
                        className="border-t border-luxury-200 hover:bg-luxury-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                              <span className="text-white font-semibold">
                                {user.username?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-luxury-900">
                              {user.username}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-luxury-600">
                            {user.email}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-luxury-900">
                            ₦{user.balance?.toLocaleString() || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-luxury-500">
                            {format(new Date(user.createdAt), "MMM dd, yyyy")}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-luxury-900">
                            {user.orders?.length || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-luxury-900">
                            ₦
                            {user.orders
                              ?.reduce((sum, o) => sum + (o.amount || 0), 0)
                              .toLocaleString() || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewUser(user._id)}
                              className="p-2 glass rounded-lg hover:bg-primary-50 transition-colors"
                              title="View Details"
                            >
                              <FiEye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 glass rounded-lg hover:bg-primary-50 transition-colors"
                              title="Edit User"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUserId(user._id);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 glass rounded-lg hover:bg-red-50 transition-colors"
                              title="Delete User"
                            >
                              <FiTrash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {adminUsersPagination.totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-luxury-200 bg-luxury-50 p-4">
                  <p className="text-sm text-luxury-500">
                    Showing page {adminUsersPagination.page} of{" "}
                    {adminUsersPagination.totalPages} ·{" "}
                    {adminUsersPagination.totalCount} users
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={adminUsersPagination.page === 1}
                      onClick={() =>
                        setPage(Math.max(1, adminUsersPagination.page - 1))
                      }
                      className="rounded-xl border border-luxury-200 bg-white px-4 py-2 text-sm text-luxury-700 hover:bg-luxury-100 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={
                        adminUsersPagination.page ===
                        adminUsersPagination.totalPages
                      }
                      onClick={() =>
                        setPage(
                          Math.min(
                            adminUsersPagination.totalPages,
                            adminUsersPagination.page + 1,
                          ),
                        )
                      }
                      className="rounded-xl border border-luxury-200 bg-white px-4 py-2 text-sm text-luxury-700 hover:bg-luxury-100 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* User Details Modal */}
          <AnimatePresence>
            {showUserModal && selectedUser && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => {
                  setShowUserModal(false);
                  dispatch(clearSelectedUser());
                }}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass max-w-2xl w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-luxury-900">
                      User Details
                    </h2>
                    <button
                      onClick={() => {
                        setShowUserModal(false);
                        dispatch(clearSelectedUser());
                      }}
                      className="text-luxury-900 hover:text-luxury-600"
                    >
                      <FiXCircle className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* User Info */}
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center">
                        <span className="text-2xl text-white font-bold">
                          {selectedUser.user?.username?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-luxury-900">
                          {selectedUser.user?.username}
                        </h3>
                        <p className="text-luxury-800">
                          {selectedUser.user?.email}
                        </p>
                        <span
                          className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(selectedUser.user?.role)}`}
                        >
                          {selectedUser.user?.role}
                        </span>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <GlassCard className="p-4">
                        <p className="text-sm text-luxury-900 mb-1">Balance</p>
                        <p className="text-xl font-bold gradient-text">
                          ₦{selectedUser.user?.balance?.toLocaleString() || 0}
                        </p>
                      </GlassCard>
                      <GlassCard className="p-4">
                        <p className="text-sm text-luxury-900 mb-1">
                          Total Orders
                        </p>
                        <p className="text-xl font-bold gradient-text">
                          {selectedUser.orders?.length || 0}
                        </p>
                      </GlassCard>
                      <GlassCard className="p-4">
                        <p className="text-sm text-luxury-900 mb-1">
                          Total Spent
                        </p>
                        <p className="text-xl font-bold gradient-text">
                          ₦
                          {selectedUser.orders
                            ?.reduce((sum, o) => sum + (o.amount || 0), 0)
                            .toLocaleString() || 0}
                        </p>
                      </GlassCard>
                      <GlassCard className="p-4">
                        <p className="text-sm text-luxury-900 mb-1">
                          Member Since
                        </p>
                        <p className="text-xl font-bold gradient-text">
                          {format(
                            new Date(selectedUser.user?.createdAt),
                            "MMM yyyy",
                          )}
                        </p>
                      </GlassCard>
                    </div>

                    {/* Recent Orders */}
                    {selectedUser.orders && selectedUser.orders.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-luxury-900 mb-4">
                          Recent Orders
                        </h3>
                        <div className="space-y-3">
                          {selectedUser.orders.slice(0, 5).map((order) => (
                            <div
                              key={order._id}
                              className="flex items-center justify-between p-3 bg-luxury-100 rounded-xl"
                            >
                              <div>
                                <p className="font-medium text-luxury-900">
                                  {order.account?.title}
                                </p>
                                <p className="text-sm text-luxury-500">
                                  {format(
                                    new Date(order.createdAt),
                                    "MMM dd, yyyy",
                                  )}
                                </p>
                              </div>
                              <span className="font-bold gradient-text">
                                ₦{order.amount?.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recent Transactions */}
                    {selectedUser.transactions &&
                      selectedUser.transactions.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-luxury-900 mb-4">
                            Recent Transactions
                          </h3>
                          <div className="space-y-3">
                            {selectedUser.transactions
                              .slice(0, 5)
                              .map((transaction) => (
                                <div
                                  key={transaction._id}
                                  className="flex items-center justify-between p-3 bg-luxury-100 rounded-xl"
                                >
                                  <div>
                                    <p className="font-medium text-luxury-900 capitalize">
                                      {transaction.type}
                                    </p>
                                    <p className="text-sm text-luxury-500">
                                      {format(
                                        new Date(transaction.createdAt),
                                        "MMM dd, yyyy",
                                      )}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <span className="font-bold gradient-text">
                                      ₦{transaction.amount?.toLocaleString()}
                                    </span>
                                    <p
                                      className={`text-xs ${
                                        transaction.status === "success"
                                          ? "text-green-600"
                                          : transaction.status === "pending"
                                            ? "text-yellow-600"
                                            : "text-red-600"
                                      }`}
                                    >
                                      {transaction.status}
                                    </p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4 border-t border-luxury-200">
                      <button
                        onClick={() => {
                          setShowUserModal(false);
                          handleEditUser(selectedUser.user);
                        }}
                        className="flex-1 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                      >
                        Edit User
                      </button>
                      <button
                        onClick={() => {
                          setShowUserModal(false);
                          setShowDeleteModal(true);
                        }}
                        className="flex-1 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                      >
                        Delete User
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Edit User Modal */}
          <AnimatePresence>
            {showEditModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowEditModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass max-w-md w-full rounded-2xl p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 className="text-xl font-semibold text-luxury-900 mb-4">
                    Edit User
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Username
                      </label>
                      <input
                        type="text"
                        value={editFormData.username}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            username: e.target.value,
                          })
                        }
                        className="input-luxury"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            email: e.target.value,
                          })
                        }
                        className="input-luxury"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Role
                      </label>
                      <select
                        value={editFormData.role}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            role: e.target.value,
                          })
                        }
                        className="input-luxury"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-luxury-700 mb-2">
                        Balance (₦)
                      </label>
                      <input
                        type="number"
                        value={editFormData.balance}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            balance: parseInt(e.target.value),
                          })
                        }
                        className="input-luxury"
                      />
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <button
                        onClick={() => setShowEditModal(false)}
                        className="flex-1 py-2 border border-luxury-300 text-luxury-600 rounded-xl hover:bg-luxury-100 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateUser}
                        className="flex-1 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Delete Confirmation Modal */}
          <AnimatePresence>
            {showDeleteModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowDeleteModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass max-w-md w-full rounded-2xl p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                      <FiAlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-luxury-900 mb-2">
                      Delete User
                    </h2>
                    <p className="text-luxury-600 mb-6">
                      Are you sure you want to delete this user? This action
                      cannot be undone.
                    </p>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowDeleteModal(false)}
                        className="flex-1 py-2 border border-luxury-300 text-luxury-600 rounded-xl hover:bg-luxury-100 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteUser}
                        className="flex-1 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};
